import { InspectionPageEstimateInput } from '../buildInspectionPageEstimateInput';
import { sectionScore } from '../inspectionComplianceClassifier';
import { INSPECTION_PDF_LABELS, INSPECTION_PDF_SAMPLE_VALUES } from './inspectionPdfLabels';
import { sectionHeading } from './inspectionPdfTypography';
import { PdfContent, pdfSinglePageColumn, pdfTable, pdfText } from './pdf/pdfBlocks';
import { PDF_CONTENT_WIDTH } from './pdf/pdfPage';
import { boldFont, regularFont } from './pdf/pdfTextMetrics';

const CAPTION_FONT = regularFont(8);
const VALUE_FONT = boldFont();
const FOOTNOTE_FONT = regularFont(7);
const FOOTNOTE_MARGIN = { top: 4 };

type KpiColumn = { caption: string; value: string };

/**
 * `PdfInspectionSummarySectionFactory`: one KPI table (captions on a header row, counts underneath), kept
 * on one page with its heading. The score column and its footnote exist only when a score can be computed.
 */
export function inspectionSummarySection(
  input: InspectionPageEstimateInput,
  sectionNumber?: number
): PdfContent {
  const { protocol } = input.source.inspection;
  const { passed, notPassed } = sectionScore(protocol);
  const hasScore = passed + notPassed > 0;
  const columns: KpiColumn[] = [
    ...INSPECTION_PDF_LABELS.summaryColumns.map((caption) => ({
      caption,
      value: String(protocol.length),
    })),
    ...(hasScore
      ? [{ caption: INSPECTION_PDF_LABELS.score, value: INSPECTION_PDF_SAMPLE_VALUES.scorePercent }]
      : []),
  ];

  return pdfSinglePageColumn([
    sectionHeading(INSPECTION_PDF_LABELS.summaryHeading, sectionNumber),
    pdfTable(
      {
        columnRatios: columns.map(() => 1),
        headerRow: columns.map(({ caption }) => ({
          content: (width) => [pdfText(caption, width, { font: CAPTION_FONT })],
        })),
        rows: [
          columns.map(({ value }) => ({
            content: (width) => [pdfText(value, width, { font: VALUE_FONT })],
          })),
        ],
      },
      PDF_CONTENT_WIDTH
    ),
    hasScore
      ? pdfText(INSPECTION_PDF_LABELS.scoreFootnote, PDF_CONTENT_WIDTH, {
          font: FOOTNOTE_FONT,
          margin: FOOTNOTE_MARGIN,
        })
      : undefined,
  ]);
}
